import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AdminTokenData } from '../types';
import { isMobileUserAgent } from '../utils/mobileUtils';
import { sessionGet } from '../utils/storage';

interface AdminPanelProps {
  isVisible: boolean;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ isVisible }) => {
  const { adminLogin, getAdminTokens, clearAdminToken, clearAllAdminTokens, sendToParent, redirectToReturnUrl, refreshAdminToken, setAdminTokenFavorite, setError: setAuthError } = useAuth();
  const [vkIdOrUid, setVkIdOrUid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminTokens, setAdminTokens] = useState<AdminTokenData[]>(getAdminTokens());
  const [refreshingToken, setRefreshingToken] = useState<string | null>(null);
  const [usingToken, setUsingToken] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Порядок: избранные закреплены сверху, дальше — по времени входа (свежие выше).
  const sortedTokens = useMemo(
    () =>
      [...adminTokens].sort((a, b) => {
        const fav = (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0);
        return fav !== 0 ? fav : b.loginTime - a.loginTime;
      }),
    [adminTokens],
  );

  const handleAdminLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vkIdOrUid.trim()) {
      setError('Введите ID');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await adminLogin(vkIdOrUid.trim());
      setAdminTokens(getAdminTokens());
      setVkIdOrUid('');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [vkIdOrUid, adminLogin, getAdminTokens]);

  // Завершение входа по админскому токену. Тот же выбор способа, что и в App.completeAuth:
  // extension → токен в URL; Telegram-webview + return_url → редирект; иначе postMessage.
  // ВАЖНО: раньше здесь была широкая проверка navigator.userAgent (Mobile/Android/iPhone),
  // но redirectToReturnUrl редиректит только при isMobileUserAgent() (Telegram-webview) —
  // в обычном мобильном браузере ветка редиректа ничего не делала, а фолбэка не было.
  const completeAdminAuth = useCallback((tokenData: AdminTokenData) => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url') || sessionGet('ssmm-return-url');
    const isMobile = isMobileUserAgent();
    const mode = urlParams.get('mode');

    if (mode === 'extension') {
      redirectToReturnUrl(tokenData.token);
    } else if (returnUrl && isMobile) {
      redirectToReturnUrl(tokenData.token);
    } else {
      sendToParent({
        type: 'AUTH_SUCCESS',
        data: {
          token: tokenData.token,
          user: tokenData.user,
        },
      });
    }
  }, [sendToParent, redirectToReturnUrl]);

  const handleUseAdminToken = useCallback(async (tokenData: AdminTokenData) => {
    setUsingToken(tokenData.token);
    setError(null);
    setAuthError(null);

    // Сначала обновляем токен, чтобы входить всегда по свежему.
    let effective = tokenData;
    try {
      effective = await refreshAdminToken(tokenData);
      setAdminTokens(getAdminTokens());
    } catch (refreshError: any) {
      // Не удалось обновить (сеть/бэк недоступен) — не блокируем вход, используем текущий токен.
      console.warn('Не удалось обновить токен перед использованием, используем текущий:', refreshError?.message);
      setAuthError(null);
    }

    try {
      completeAdminAuth(effective);
    } catch (error) {
      console.error('Error using admin token:', error);
    } finally {
      setUsingToken(null);
    }
  }, [refreshAdminToken, getAdminTokens, completeAdminAuth, setAuthError]);

  const handleToggleFavorite = useCallback((tokenData: AdminTokenData) => {
    setAdminTokens(setAdminTokenFavorite(tokenData.token, !tokenData.favorite));
  }, [setAdminTokenFavorite]);

  const handleClearToken = useCallback((tokenToRemove: string) => {
    clearAdminToken(tokenToRemove);
    setAdminTokens(getAdminTokens());
  }, [clearAdminToken, getAdminTokens]);

  const handleClearAllTokens = useCallback(() => {
    clearAllAdminTokens();
    setAdminTokens([]);
  }, [clearAllAdminTokens]);

  const handleRefreshToken = useCallback(async (tokenData: AdminTokenData) => {
    try {
      setRefreshingToken(tokenData.token);
      setError(null);

      await refreshAdminToken(tokenData);
      setAdminTokens(getAdminTokens());
    } catch (error: any) {
      setError(error.message);
    } finally {
      setRefreshingToken(null);
    }
  }, [refreshAdminToken, getAdminTokens]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!isVisible) return null;

  // Кнопка-иконка для открытия панели
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-50 w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors"
        title="Админ-панель"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    );
  }

  // Модальная панель
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-zinc-900 rounded-2xl border border-zinc-700 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-zinc-700 bg-red-500/10">
          <h3 className="text-sm font-medium text-red-400">Админ-панель</h3>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {/* Login form */}
          <form onSubmit={handleAdminLogin} className="space-y-3">
            <div>
              <input
                type="text"
                value={vkIdOrUid}
                onChange={(e) => setVkIdOrUid(e.target.value)}
                placeholder="VK ID или Telegram UID"
                className="w-full px-3 py-2.5 text-sm bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-red-500/50"
                disabled={isLoading}
              />
            </div>
            
            {error && (
              <div className="p-2 text-xs text-red-400 bg-red-500/10 rounded-lg border border-red-500/30">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading || !vkIdOrUid.trim()}
              className="w-full px-4 py-2.5 text-sm font-medium text-white bg-red-500/20 border border-red-500/30 rounded-xl hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Вход...' : 'Войти как пользователь'}
            </button>
          </form>

          {/* Saved tokens */}
          {adminTokens.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs text-zinc-500">Сохранённые ({adminTokens.length})</span>
                <button
                  onClick={handleClearAllTokens}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Очистить
                </button>
              </div>
              
              <div className="space-y-2">
                {sortedTokens.map((tokenData) => {
                  const isBusy = usingToken === tokenData.token || refreshingToken === tokenData.token;
                  return (
                  <div key={tokenData.token} className="p-3 bg-zinc-800 rounded-xl">
                    <div className="flex justify-between items-start mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">
                          {tokenData.user.name || 'Неизвестный'}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {tokenData.user.vkId || tokenData.user.uid} • {formatDate(tokenData.loginTime)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleFavorite(tokenData)}
                        className="flex-shrink-0 p-1 -mt-1 -mr-1 transition-colors"
                        title={tokenData.favorite ? 'Убрать из избранного' : 'В избранное'}
                      >
                        <svg
                          className={`w-4 h-4 ${tokenData.favorite ? 'text-amber-400' : 'text-zinc-600 hover:text-amber-400'}`}
                          fill={tokenData.favorite ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleUseAdminToken(tokenData)}
                        disabled={isBusy}
                        className="flex-1 px-2 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {usingToken === tokenData.token ? 'Обновление…' : 'Использовать'}
                      </button>
                      <button
                        onClick={() => handleRefreshToken(tokenData)}
                        disabled={isBusy}
                        className="px-2 py-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                        title="Обновить"
                      >
                        {refreshingToken === tokenData.token ? '...' : '↻'}
                      </button>
                      <button
                        onClick={() => handleClearToken(tokenData.token)}
                        disabled={isBusy}
                        className="px-2 py-1.5 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
